// src/components/layout/TaskDetail.jsx
import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Calendar, 
  Users, 
  BookOpen, 
  Target,
  Download,
  Eye,
  ArrowLeft,
  Edit,
  Share2,
  Lock,
  Tag,
  Brain,
  Zap
} from 'lucide-react';
import { AssessmentService } from '../../services/taskService';
import { useAuth } from '../../contexts/AuthContext';
import TaskMarkList from './TaskMarkList';

const TaskDetail = ({ task, taskId, onBack, onEdit }) => {
  const { currentUser } = useAuth();
  const [taskData, setTaskData] = useState(task);
  const [loading, setLoading] = useState(!task && !!taskId);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (taskId && !task) {
      loadTask();
    }
  }, [taskId, task]);

  const loadTask = async () => {
    try {
      setLoading(true);
      const fetchedTask = await AssessmentService.getAssessmentTask(taskId);
      setTaskData(fetchedTask);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const isTaskOwner = (task) => {
    return task?.createdBy === currentUser?.uid;
  };

  const getTaxonomyLevelInfo = (level) => {
    const levels = {
      'L1': { label: 'L1 - Remember', color: 'bg-gray-100 text-gray-800', description: 'Recall facts and basic concepts' },
      'L2': { label: 'L2 - Understand', color: 'bg-blue-100 text-blue-800', description: 'Explain ideas or concepts' },
      'L3': { label: 'L3 - Apply', color: 'bg-yellow-100 text-yellow-800', description: 'Use information in new situations' },
      'L4': { label: 'L4 - Analyze', color: 'bg-red-100 text-red-800', description: 'Draw connections among ideas' }
    };
    return levels[level] || levels['L1'];
  };

  const getTypeColor = (type) => {
    const colors = {
      'Exam': 'bg-red-100 text-red-800',
      'Test': 'bg-orange-100 text-orange-800',
      'Project': 'bg-purple-100 text-purple-800',
      'Assignment': 'bg-blue-100 text-blue-800',
      'Investigation': 'bg-green-100 text-green-800',
      'Other': 'bg-gray-100 text-gray-800'
    };
    return colors[type] || colors['Other'];
  };

  const calculateQuestionStats = () => {
    if (!taskData?.questions) return { totalQuestions: 0, totalSubquestions: 0, taxonomyDistribution: {} };
    
    let totalQuestions = taskData.questions.length;
    let totalSubquestions = taskData.questions.reduce((sum, q) => sum + (q.subquestions?.length || 0), 0);
    let taxonomyDistribution = { L1: 0, L2: 0, L3: 0, L4: 0 };

    taskData.questions.forEach(question => {
      if (question.subquestions?.length > 0) {
        question.subquestions.forEach(sub => {
          taxonomyDistribution[sub.taxonomyLevel || 'L1']++;
        });
      } else {
        taxonomyDistribution[question.taxonomyLevel || 'L1']++;
      }
    });

    return { totalQuestions, totalSubquestions, taxonomyDistribution };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">{error}</div>
        <button
          onClick={onBack}
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-blue-700"
        >
          Go Back
        </button>
      </div>
    );
  }

  if (!taskData) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-600 mb-4">Task not found</div>
        <button
          onClick={onBack}
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-blue-700"
        >
          Go Back
        </button>
      </div>
    );
  }

  const stats = calculateQuestionStats();

  return (
    <div className="flex-1 px-2 py-8">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-4">
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-md"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-gray-900">{taskData.name}</h1>
                {!isTaskOwner(taskData) && (
                  <Share2 className="w-5 h-5 text-blue-500" title="Shared with you" />
                )}
                {isTaskOwner(taskData) && taskData.sharedWith?.length > 0 && (
                  <Users className="w-5 h-5 text-green-500" title={`Shared with ${taskData.sharedWith.length} users`} />
                )}
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getTypeColor(taskData.type)}`}>
                  {taskData.type}
                </span>
                <span className="font-medium">{taskData.totalMarks} marks</span>
                {taskData.papers && taskData.papers.length > 0 && (
                  <span className="text-gray-500">• {taskData.papers.join(', ')}</span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {isTaskOwner(taskData) && onEdit && (
              <button
                onClick={() => onEdit(taskData)}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-blue-700"
              >
                <Edit className="w-4 h-4" />
                Edit Task
              </button>
            )}
            {!isTaskOwner(taskData) && (
              <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-md">
                <Lock className="w-4 h-4" />
                Read Only
              </div>
            )}
          </div>
        </div>

        {/* Basic Information Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-gray-500" />
            <div>
              <p className="text-sm text-gray-500">Grade & Subject</p>
              <p className="font-medium">{taskData.grade} • {taskData.subject}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-gray-500" />
            <div>
              <p className="text-sm text-gray-500">
                {isTaskOwner(taskData) ? 'Created' : 'Shared'}
              </p>
              <p className="font-medium">
                {taskData.createdAt ? new Date(taskData.createdAt).toLocaleDateString() : 'Unknown'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-gray-500" />
            <div>
              <p className="text-sm text-gray-500">Questions</p>
              <p className="font-medium">
                {stats.totalQuestions} 
                {stats.totalSubquestions > 0 && ` (${stats.totalSubquestions} parts)`}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Target className="w-5 h-5 text-gray-500" />
            <div>
              <p className="text-sm text-gray-500">Files Attached</p>
              <p className="font-medium">{taskData.taskFiles?.length || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Topics */}
      {taskData.topics && taskData.topics.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Topics Covered
          </h2>
          <div className="flex flex-wrap gap-2">
            {taskData.topics.map((topic, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full"
              >
                {topic}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Task Files */}
      {taskData.taskFiles && taskData.taskFiles.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Task Files
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {taskData.taskFiles.map((file, index) => (
              <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="font-medium text-gray-900">{file.name}</p>
                    <p className="text-sm text-gray-500">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <a
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-md"
                  title="View file"
                >
                  <Eye className="w-4 h-4" />
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Taxonomy Distribution */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Target className="w-5 h-5" />
          Difficulty Level Distribution
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(stats.taxonomyDistribution).map(([level, count]) => {
            const levelInfo = getTaxonomyLevelInfo(level);
            return (
              <div key={level} className="text-center p-4 bg-gray-50 rounded-lg">
                <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium mb-2 ${levelInfo.color}`}>
                  {levelInfo.label}
                </div>
                <p className="text-2xl font-bold text-gray-900">{count}</p>
                <p className="text-xs text-gray-500">{levelInfo.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Questions Breakdown */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Questions Breakdown
        </h2>
        
        <div className="space-y-6">
          {taskData.questions?.map((question, qIndex) => (
            <div key={qIndex} className="border border-gray-200 rounded-lg p-6 bg-gray-50">
              {/* Question Header */}
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    Question {question.questionNumber}
                  </h3>
                  <div className="flex items-center gap-4 mt-2">
                    <span className="text-sm text-gray-600">
                      <strong>Topic:</strong> {question.topic || 'Not specified'}
                    </span>
                    {question.subtopic && (
                      <span className="text-sm text-gray-600">
                        <strong>Subtopic:</strong> {question.subtopic}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  {question.subquestions?.length > 0 ? (
                    <div>
                      <span className="text-lg font-bold text-gray-900">
                        {question.subquestions.reduce((sum, sub) => sum + (sub.marks || 0), 0)}
                      </span>
                      <span className="text-sm text-gray-500 ml-1">marks (total)</span>
                    </div>
                  ) : (
                    <div>
                      <span className="text-lg font-bold text-gray-900">{question.marks}</span>
                      <span className="text-sm text-gray-500 ml-1">marks</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Question Details (only if no subquestions) */}
              {(!question.subquestions || question.subquestions.length === 0) && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getTaxonomyLevelInfo(question.taxonomyLevel).color}`}>
                      {getTaxonomyLevelInfo(question.taxonomyLevel).label}
                    </div>
                  </div>
                  
                  {/* Tags, Concepts, Skills for main question */}
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                      <Tag className="w-4 h-4" /> Tags
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {question.tags?.filter(tag => tag.trim()).map((tag, index) => (
                        <span key={index} className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded">
                          {tag}
                        </span>
                      )) || <span className="text-xs text-gray-500">None</span>}
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                      <Brain className="w-4 h-4" /> Concepts
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {question.concepts?.filter(concept => concept.trim()).map((concept, index) => (
                        <span key={index} className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                          {concept}
                        </span>
                      )) || <span className="text-xs text-gray-500">None</span>}
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                      <Zap className="w-4 h-4" /> Skills
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {question.skills?.filter(skill => skill.trim()).map((skill, index) => (
                        <span key={index} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded">
                          {skill}
                        </span>
                      )) || <span className="text-xs text-gray-500">None</span>}
                    </div>
                  </div>
                </div>
              )}

              {/* Subquestions */}
              {question.subquestions && question.subquestions.length > 0 && (
                <div className="ml-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Subquestions</h4>
                  <div className="space-y-4">
                    {question.subquestions.map((subquestion, subIndex) => (
                      <div key={subIndex} className="bg-white rounded-lg p-4 border border-gray-200">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h5 className="font-medium text-gray-900">
                              Question {subquestion.questionNumber}
                            </h5>
                            <div className="flex items-center gap-4 mt-1">
                              <span className="text-sm text-gray-600">
                                <strong>Topic:</strong> {subquestion.topic || 'Not specified'}
                              </span>
                              {subquestion.subtopic && (
                                <span className="text-sm text-gray-600">
                                  <strong>Subtopic:</strong> {subquestion.subtopic}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-lg font-bold text-gray-900">{subquestion.marks}</span>
                            <span className="text-sm text-gray-500 ml-1">marks</span>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div>
                            <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getTaxonomyLevelInfo(subquestion.taxonomyLevel).color}`}>
                              {getTaxonomyLevelInfo(subquestion.taxonomyLevel).label}
                            </div>
                          </div>
                          
                          <div>
                            <p className="text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                              <Tag className="w-3 h-3" /> Tags
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {subquestion.tags?.filter(tag => tag.trim()).map((tag, index) => (
                                <span key={index} className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded">
                                  {tag}
                                </span>
                              )) || <span className="text-xs text-gray-500">None</span>}
                            </div>
                          </div>
                          
                          <div>
                            <p className="text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                              <Brain className="w-3 h-3" /> Concepts
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {subquestion.concepts?.filter(concept => concept.trim()).map((concept, index) => (
                                <span key={index} className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                                  {concept}
                                </span>
                              )) || <span className="text-xs text-gray-500">None</span>}
                            </div>
                          </div>
                          
                          <div>
                            <p className="text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                              <Zap className="w-3 h-3" /> Skills
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {subquestion.skills?.filter(skill => skill.trim()).map((skill, index) => (
                                <span key={index} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded">
                                  {skill}
                                </span>
                              )) || <span className="text-xs text-gray-500">None</span>}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Ownership Footer */}
      <div className="bg-white rounded-lg shadow p-4">
        <p className="text-sm text-gray-600 text-center">
          {isTaskOwner(taskData) ? (
            <>
              Created by you on {taskData.createdAt ? new Date(taskData.createdAt).toLocaleDateString() : 'Unknown date'}
              {taskData.sharedWith?.length > 0 && (
                <span className="ml-2 text-green-600">
                  • Shared with {taskData.sharedWith.length} user{taskData.sharedWith.length !== 1 ? 's' : ''}
                </span>
              )}
            </>
          ) : (
            <span className="text-blue-600">This task has been shared with you</span>
          )}
        </p>
      </div>
    </div>
  );
};

export default TaskDetail;